import { Component, effect, OnDestroy, OnInit, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DynamicFilterConfig, DynamicFilterValues, ModalService, createDynamicFilterState } from '@card-software/library';
import { IMEXAdminGroup, IMEXViewerGroup } from '../../../models/roles/roles-group';
import { Invoice, InvoiceSearchCommand } from '../../../models/invoice/invoice';
import { InvoiceStatus } from '../../../models/invoice/invoice-status-enum';
import { PurchaseOrder } from '../../../models/purchase-order/purchase-order';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { PurchaseOrderProxyService } from '../../../proxies/order/purchase-order.proxy.service';
import { InvoiceDetailModalComponent } from '../../_modals/invoice-detail-modal/invoice-detail-modal.component';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss',
})
export class InvoicesComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translate = inject(TranslateService);
  private modalService = inject(ModalService);
  private invoiceProxyService = inject(InvoiceProxyService);
  private purchaseOrderProxyService = inject(PurchaseOrderProxyService);

  isLoading = signal(0);
  listInvoices = signal<Invoice[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'invoice-filters', config: this.filterConfig });
  showFilters = signal(false);
  purchaseOrders = signal<PurchaseOrder[]>([]);
  filteredPurchaseOrders = signal<PurchaseOrder[]>([]);
  currentPage = signal<number>(1);
  totalRecords = signal<number>(0);
  pageSize = signal<number>(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  canEdit = computed(() => {
    if (AuthService.hasRole(IMEXAdminGroup.InvoiceAdmin)) {
      return true;
    }
    if (AuthService.hasRole(IMEXViewerGroup.InvoiceViewer)) {
      return false;
    }
    return false;
  });

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;
  private lastSearchCriteria: InvoiceSearchCommand = {};

  getRowData(rowData: unknown): Invoice {
    return rowData as Invoice;
  }

  getStatusLabel(status: number): string {
    return InvoiceStatus[status] ?? String(status);
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        if (this.translate.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadPurchaseOrders();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'invoiceNumber',
          label: translate('Invoices_Invoice_Number', 'Invoice Number'),
          placeholder: translate('Enter_invoice_number', 'Enter invoice number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'providerInvoiceNumber',
          label: translate('Invoices_Provider_Invoice_Number', 'Provider Invoice Number'),
          placeholder: translate('Enter_provider_invoice_number', 'Enter provider invoice number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'purchaseOrderReference',
          label: translate('Invoices_Purchase_Order', 'Purchase Order'),
          placeholder: translate('Select_purchase_order', 'Select purchase order'),
          autocompleteConfig: {
            suggestions: this.filteredPurchaseOrders(),
            optionLabel: 'purchaseOrderNumber',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterPurchaseOrders(event),
            resolveById: (id: string) => this.resolvePurchaseOrder(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolvePurchaseOrder(id: string): Observable<PurchaseOrder | null> {
    return this.purchaseOrderProxyService.getPurchaseOrderById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private loadPurchaseOrders(): void {
    this.purchaseOrderProxyService.getPurchaseOrder().subscribe({
      next: (response) => {
        const purchaseOrders = response.data ?? [];
        this.purchaseOrders.set(purchaseOrders);
        this.filteredPurchaseOrders.set(purchaseOrders);
      },
      error: (e) => {
        console.error('Error loading purchase orders:', e);
        this.purchaseOrders.set([]);
        this.filteredPurchaseOrders.set([]);
      },
    });
  }

  filterPurchaseOrders(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.purchaseOrders().filter((po) => po.purchaseOrderNumber?.toLowerCase().includes(query));
    this.filteredPurchaseOrders.set(filtered);
  }

  private onSearch(searchCriteria: InvoiceSearchCommand): void {
    this.startLoading();
    this.lastSearchCriteria = searchCriteria;
    this.invoiceProxyService
      .searchInvoice(searchCriteria, this.currentPage(), this.pageSize(), false, this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const pagedResult = response.data;
          this.listInvoices.set(pagedResult?.items ?? []);
          this.totalRecords.set(pagedResult?.totalCount ?? 0);
        },
        error: (e) => {
          console.error(e);
          this.listInvoices.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = this.normalizeSortField(event.field, this.sortField());
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: InvoiceSearchCommand = {
      invoiceNumber: (filters['invoiceNumber'] as string)?.trim() || undefined,
      providerInvoiceNumber: (filters['providerInvoiceNumber'] as string)?.trim() || undefined,
      purchaseOrderReference:
        (filters['purchaseOrderReference'] as Record<string, string>)?.['id'] || (filters['purchaseOrderReference'] as string) || undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  public redirectToDetails(orderId: string): void {
    const queryParams = { id: orderId };
    this.router.navigate(['/invoice-detail'], { queryParams });
  }

  public openModalInvoice(invoice: Invoice): void {
    this.modalService.open(InvoiceDetailModalComponent, { modalClass: 'modal_small' }, { invoice: invoice ?? null }).subscribe();
  }

  private normalizeSortField(sortField: string | string[] | undefined | null, fallback: string | undefined): string | undefined {
    if (Array.isArray(sortField)) {
      return sortField[0] ?? fallback;
    }
    return sortField ?? fallback;
  }
}
